import { DateTime } from "luxon";
import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { AttachedDocumentList } from "@/Common/Components/IconControls/AttachedDocumentList";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { Popup } from "@/Common/Components/Modals/Popup";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { FileType } from "@/Common/Models/FileType";
import { Guid } from "@/Common/Models/Guid";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import { Comment } from "@/Common/Models/Patient/Comment";
import { LabRequestType } from "@/Common/Models/Patient/LabRequest";
import { IPatientCliniciansModel } from "@/Common/Models/Patient/UI/Profile/IPatientCliniciansModel";
import { Person } from "@/Common/Models/Person/Person";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { FaxAndSignatureClinicianSelectionState } from "@/ExportableFile/Components/FaxAndSignatureClinicianSelection";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { ExportableFileDisplayOption, ExportableFileModel } from "@/ExportableFile/Models/ExportableFileTableItem";
import { FileTypeMedicalSummary } from "@/ExportableFile/Models/FileTypes/FileTypeMedicalSummary";
import { FileTypeMedicationSummary } from "@/ExportableFile/Models/FileTypes/FileTypeMedicationSummary";
import { IFaxPrintExportableFileConfig } from "@/ExportableFile/Models/IFaxPrintExportableFileConfig";
import { SignPrintAuthorizationOverlayElement } from "@/ExportableFile/Models/OverlayElements/SignPrintAuthorizationOverlayElement";
import { MedicalItemScreeningTest, MedicalItemScreeningTestWithLink } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { MedicalRequestFormComponent } from "@/Portfolio/Components/MedicalRequestForm/MedicalRequestFormComponent";
import { ConsultationRequest } from "@/Portfolio/Models/ConsultationRequest";
import { IMedicalRequest } from "@/Portfolio/Models/IMedicalRequest";
import { LabRequest } from "@/Portfolio/Models/LabRequest";
import { MedicalRequestStatus } from "@/Portfolio/Models/MedicalRequestStatus";
import { PatientProfileRequestModalSettingsModel } from "@/Portfolio/Models/PatientProfileRequestModalSettingsModel";
import { PrintInfo } from "@/Portfolio/Models/PrintInfo";
import { AttachDocumentToRequestComponent } from "./AttachDocumentToRequestComponent";
import { LinkCareElementComponent } from "./LinkCareElementComponent";
import { IMedicalRequestAction, LabRequestAction, LinkedCareElementsAction, LinkingCareElementRequest } from "./MedicalRequestAction";
import { MedicalRequestFactory } from "./MedicalRequestFactory";
import { MedicalRequestFormRowComponent } from "./MedicalRequestFormRow";
import { IHandleMedicalRequest } from "./MedicalRequestHandler";
import { MedicalRequestHeaderComponent } from "./MedicalRequestHeaderComponent";
import { MedicalRequestStatusSection } from "./MedicalRequestStatusSection";
import { MedicalRequestViewComponent } from "./MedicalRequestViewComponent";

import stylesImport from "./MedicalRequestPopupController.module.scss";
import legacyStylesImport from "./MedicalRequestPopupControllerLegacy.module.scss";

interface IMedicalRequestButtonProperties {
    show?: boolean;
    disabled?: boolean;
    text?: string;
    onClick?: (e?: React.MouseEvent) => void;
}

interface IButtonProperties {
    show: boolean;
    disabled: boolean;
    text: string;
    onClick(e?: React.MouseEvent): void;
}

interface IMedicalRequestFooterButton {
    printButton?: IButtonProperties;
    deleteButton?: IButtonProperties;
    cancelButton?: IButtonProperties;
    portalButton?: IButtonProperties;
    faxButton?: IButtonProperties;
    signButton?: IButtonProperties;
    useSignedCopyButton?: IButtonProperties;
    sendButton?: IButtonProperties;
    saveButton?: IButtonProperties;
    invalidButton?: IButtonProperties;
    editButton?: IButtonProperties;
    reinstateButton?: IButtonProperties;
}

interface IMedicalRequestEncounterProps {
    id: string;
    consultationDate: Date;
    isSignedOff: boolean;
    SignedOffDate?: Date;
    SignedByEmployeeId?: Guid;
}

interface IMedicalRequestPopupControllerProps {
    type: MedicalRequestType;

    id?: string;
    encounter?: IMedicalRequestEncounterProps;
    patientDoB?: Date;
    // these are needed for labrequest christus
    siteId?: string;
    labRequestType?: LabRequestType;

    patientContactIdForNewRequests: Guid;

    showPrintHeader?: boolean;
    isDisplayAsReadonly?: boolean;
    closeAfterButtonClick?: boolean;
    hideLinkedCareSection?: boolean;

    onClose: () => void;
    onSaveSuccess?: (model: IMedicalRequest, shouldSaveRelatedEmployee: boolean, printInfo: PrintInfo) => void;
    onSendCallback?: (model: IMedicalRequest, sendStatus: string) => void;
    onDeleteSuccess?: (requestId: Guid, encounterId: Guid) => void;
    onPrintSuccess?: (fileName: ExportableFile, id?: string) => void;
    onSendToPortal?: (model: IMedicalRequest, unsignedFileName?: string, signedFileName?: string) => void;
    onToggleIsInvalid?: (id: Guid, encounterId: Guid, isInvalid: boolean, sharedItemId: string) => void;
    onSignClick?: (model: IMedicalRequest, printInfo: PrintInfo) => void;
    onUseSignedCopyClick?: (model: IMedicalRequest, printInfo: PrintInfo) => void;
    onFaxPrintPopupClose?: () => void;

    loadSignedAuthorizations?: (id: string) => void;

    sharedItem?: SharedItem;
    sharedItemMessages?: SharedItemMessage[];
    isPatientRegisteredToPortal?: boolean;
}

class MedicalRequestPopupControllerState {
    public model: IMedicalRequest;
    public initialModel: IMedicalRequest;

    public printInfoModel: PrintInfo;
    public comment: string;

    public careElements: MedicalItemScreeningTest[];
    public isCareElementsLoaded: boolean;
    public linkedElement: MedicalItemScreeningTestWithLink;
    public selectedCareElement: MedicalItemScreeningTest;

    // use for christus
    public selectedCostCenterId: string;

    public currentEmployee: EmployeeUIModel;
    public showEditOption: boolean;

    public isButtonAlreadyClicked: boolean;

    // Fax related state
    public fileName: ExportableFile;
    public isFaxValid: boolean;
    public faxName: string;
    public faxNumber: string;
    public patientClinicianModel: IPatientCliniciansModel;
    public clinicianInfoState: FaxAndSignatureClinicianSelectionState;
    public showFaxAndSignPopup: boolean;
    public showLinkRemovalWarning: boolean;
    public showDeleteWarning: boolean;
    public showSendFaxResponsePopup: boolean;
    public isSentFaxSuccess: boolean;
    public willPrintAfterFax: boolean;

    public sharedItemId: string;
    public sharedItem: SharedItem;
    public sharedItemMessages: SharedItemMessage[];

    public saveButtonClicked: boolean;
    public isReasonClickableTextPanelOpen: boolean;
    public isDescriptionClickableTextPanelOpen: boolean;
    public attachments: ExportableFileModel[];
}

class MedicalRequestPopupController extends BaseReactComponent<IMedicalRequestPopupControllerProps, MedicalRequestPopupControllerState> {
    private _id = "MedicalRequestPopupController";
    private _medicalRequestAction: IMedicalRequestAction;
    private _medicalRequestHandler: IHandleMedicalRequest;

    private readonly styles = cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements
        ? stylesImport
        : legacyStylesImport;

    constructor(props: IMedicalRequestPopupControllerProps) {
        super(props);

        this._medicalRequestAction = MedicalRequestFactory.createAction(props.type);
        this._medicalRequestHandler = MedicalRequestFactory.createHandler(props.type);

        const initialState = new MedicalRequestPopupControllerState();
        initialState.currentEmployee = sessionStore.getCurrentEmployee();
        initialState.selectedCareElement = null;
        initialState.careElements = [];
        initialState.fileName = null;
        initialState.isFaxValid = false;
        initialState.showLinkRemovalWarning = false;
        initialState.showSendFaxResponsePopup = false;
        initialState.isSentFaxSuccess = false;
        initialState.willPrintAfterFax = false;
        initialState.saveButtonClicked = false;
        initialState.sharedItemId = null;
        initialState.sharedItem = new SharedItem();
        initialState.sharedItemMessages = [];
        initialState.isReasonClickableTextPanelOpen = false;
        initialState.isDescriptionClickableTextPanelOpen = false;
        initialState.attachments = [];
        this.state = initialState;
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
    }

    public componentDidMount(): void {
        super.setOverlay("MedicalRequestPopupController");

        if (!this.isNewForm)
            window.setTimeout(() => this.load(this.props.id), 10);
        else
            this.initNewModel(this.props.encounter.id, this.props.patientContactIdForNewRequests);

        if (cacheStore.ClinicSettings.EnableFollowUps)
            window.setTimeout(() => this.loadCareElements(this.props.encounter?.id), 10);
    }

    private mutateModelState(mutation: (m: IMedicalRequest) => void, then?: () => void): void {
        this.mutateState(
            s => {
                s.model = this.state.model;
                mutation(s.model);
            },
            then
        );
    }

    public render(): JSX.Element {
        if (!this.isNewForm && !this.state.model)
            return null;

        if (this.isNewForm && !this.props.encounter.id)
            return null;

        const bodyClassName = `p-body medicalrequest-body container-fluid ${this.styles.requestGridContainer} ${this.styles.consultationAndExamRequestLayout}`;

        return (
            <div
                className="medical-request-popup-controller-container"
                onClick={() => this.onCloseAllClickableTextPanel()}
            >
                <Popup
                    Id={this._id}
                    title={cacheStore.GetString(this._medicalRequestHandler.titleLabel)}
                    onCloseDelegate={() => this.props.onClose?.()}
                    cssClass="portfolio request-popup container"
                    customFooter={this.renderFooter()}
                    topPosition={50}
                >
                    <div className={bodyClassName} id={this.styles.medicalRequestForm}>
                        {this.renderBody()}
                    </div>
                </Popup>
                <div>
                    {this.state.showLinkRemovalWarning &&
                        this.renderLinkRemovalWarningModalWindow()
                    }
                    {this.state.showDeleteWarning &&
                        this.renderDeleteWarningModalWindow()
                    }
                </div>
            </div>
        );
    }

    private renderBody(): JSX.Element {
        if (this.isMedicalFormReadOnly()) {
            const render = [];
            if (this.props.showPrintHeader)
                render.push(<MedicalRequestHeaderComponent key="medicalrequest-header" model={this.state.printInfoModel} />);

            render.push(
                <MedicalRequestViewComponent
                    key="medicalrequest-view"
                    model={this.state.model}
                    disableDescriptionMaxHeight={this.isInvalid}
                    onRecipientChange={recipient => this.mutateModelState((m: IMedicalRequest) => m.recipient = recipient)}
                    onRecipientSearchTextChange={searchText => this.mutateModelState((m: IMedicalRequest) => {
                        m.recipientContactDirectoryEntityId = null;
                        m.recipientDirectorySelectedEntity = null;
                        m.recipient = searchText;
                        m.recipientSearchText = searchText;
                    })}
                    onContactDirectoryEntityChanged={directoryEntity => this.mutateModelState((state: IMedicalRequest) => {
                        state.recipientContactDirectoryEntityId = directoryEntity.id;
                        state.recipient = directoryEntity.model.getDisplayName();
                        state.recipientDirectorySelectedEntity = directoryEntity;
                        state.recipientContactDirectoryEntityType = directoryEntity.type;
                    })}
                    onClearRecipient={() => this.mutateModelState(p => {
                        p.recipientContactDirectoryEntityId = null;
                        p.recipient = null;
                        p.recipientDirectorySelectedEntity = null;
                        p.recipientContactDirectoryEntityType = null;
                        p.recipientSearchText = null;
                    })}
                    attachDocumentComponent={this.renderAttachDocumentComponent()}
                    editContentWarningMessageLabel={this.isPrintedOrFaxedOrSyncedToPortal || this.isEncounterSignedOff
                        ? this._medicalRequestHandler.unableToModifyWarningLabel
                        : null
                    }
                />
            );

            const systemMessageRender = this.renderSystemMessages();

            if (systemMessageRender) {
                render.push(systemMessageRender);
            }

            return (
                <>
                    {render}
                    {this.renderStatusSection()}
                    {this.renderLinkCareElement()}
                </>
            );
        }

        return (
            <>
                <MedicalRequestFormComponent
                    model={this.state.model}
                    siteId={this.props.siteId}
                    costCenterId={this.state.selectedCostCenterId}
                    disabled={this.state.showEditOption}
                    onRequestingStaffChange={contactFacetId => this.mutateModelState(m => m.requestingStaffContactFacetId = contactFacetId)}
                    onSpecialityChange={text => this.mutateModelState((m: ConsultationRequest) => m.speciality = text)}
                    onPriorityChange={id => this.mutateModelState(m => m.priorityDropDownId = id)}
                    onReasonChange={text => this.mutateModelState(m => m.reason = text)}
                    onDescriptionChange={text => this.mutateModelState(m => m.description = text)}
                    onRecipientChange={recipient => this.mutateModelState((m: IMedicalRequest) => m.recipient = recipient)}
                    onRecipientSearchTextChange={searchText => this.mutateModelState((m: IMedicalRequest) => {
                        m.recipientContactDirectoryEntityId = null;
                        m.recipientDirectorySelectedEntity = null;
                        m.recipient = searchText;
                        m.recipientSearchText = searchText;
                    })}
                    onContactDirectoryEntityChanged={directoryEntity => this.mutateModelState((state: IMedicalRequest) => {
                        state.recipientContactDirectoryEntityId = directoryEntity.id;
                        state.recipient = directoryEntity.model.getDisplayName();
                        state.recipientDirectorySelectedEntity = directoryEntity;
                        state.recipientContactDirectoryEntityType = directoryEntity.type;
                    })}
                    onClearRecipient={() => this.mutateModelState(p => {
                        p.recipientContactDirectoryEntityId = null;
                        p.recipient = null;
                        p.recipientDirectorySelectedEntity = null;
                        p.recipientContactDirectoryEntityType = null;
                        p.recipientSearchText = null;
                    })}
                    onDueDateChange={date => {
                        this.mutateModelState(m => {
                            m.dueDateText = date.text;
                            m.dueDate = date.value;
                        });
                    }}
                    onBillCodesChange={billCodes => this.mutateState(s => {
                        s.model = this.state.model;
                        (s.model as LabRequest).billCodes = billCodes;
                        s.selectedCostCenterId = billCodes.length > 0 ? billCodes.firstOrDefault().CostCenterId : null;
                    })}
                    onCostCenterChange={id => this.mutateState(s => {
                        s.model = this.state.model;
                        (s.model as LabRequest).billCodes = [];
                        s.selectedCostCenterId = id;
                    })}
                    isReasonClickableTextPanelOpen={this.state.isReasonClickableTextPanelOpen}
                    setReasonClickableTextPanelVisibility={(isVisible: boolean) =>
                        this.mutateState(p => {
                            p.isReasonClickableTextPanelOpen = isVisible;
                        })
                    }
                    isDescriptionClickableTextPanelOpen={this.state.isDescriptionClickableTextPanelOpen}
                    setDescriptionClickableTextPanelVisibility={(isVisible: boolean) =>
                        this.mutateState(p => {
                            p.isDescriptionClickableTextPanelOpen = isVisible;
                        })
                    }
                    onCloseAllClickableTextPanel={() => this.onCloseAllClickableTextPanel()}
                    attachDocumentComponent={this.renderAttachDocumentComponent()}
                    type={this.props.type}
                />

                {this.renderStatusSection()}
                {this.renderLinkCareElement()}
                <div className="flex-container" style={{ justifyContent: "flex-end", marginRight: "7px" }}>
                    {this.renderFooterButton(this.footerButtons.editButton, "btn btn-primary")}
                </div>
            </>
        );
    }

    private renderAttachDocumentComponent(): JSX.Element {
        if (!cacheStore.ClinicSettings.EnableConsultationExamRequestAttachments) return null;

        const canEditAttachments =
        (
            (cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements || cacheStore.ClinicSettings.EnableDefaultAttachmentsOnConsultationRequests)
            && this.isConsultationRequest
        )
        || (!this.isMedicalFormReadOnly() && !this.state.showEditOption);

        if (!canEditAttachments && !this.state.attachments?.any())
            return null;

        return (
            <FeatureFlagControl
                flag={canEditAttachments}
                activeFeature={(
                    <AttachDocumentToRequestComponent
                        onAttachmentChange={attachment => this.mutateState(s => s.attachments = attachment)}
                        removeAttachmentFromList={itemId => this.mutateState(s => s.attachments = s.attachments.filter(x => x.itemId !== itemId))}
                        selectedAttachments={this.state.attachments}
                        patient={new Person({ ContactId: this.state.printInfoModel?.patientContactId ?? this.props.patientContactIdForNewRequests })}
                    />
                )}
                fallbackFeature={(
                    <div className={this.styles.attachedDocumentListWrapper}>
                        <AttachedDocumentList
                            selectedAttachments={this.state.attachments}
                            resolveDescription={p => p.displayDescription}
                        />
                    </div>
                )}
            />
        );
    }

    private renderSystemMessages(): JSX.Element {
        let render = [];
        let generatedByRender = null;

        if (!cacheStore.ClinicSettings.EnableConsultationRequestHistory && this.canBuildGeneratedBy(this.state.model)) {
            generatedByRender = this.renderGenerateBy(this.state.model);
        }

        if (generatedByRender)
            render.push(generatedByRender);

        if (!this.isInvalid && !cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements) {
            if (this.props.showPrintHeader)
                render.push(this.renderAbleToModifyFromPatientFileOnlyWarning("able-to-modify-warning"));
            else if (this.isPrintedOrFaxedOrSyncedToPortal)
                render.push(this.renderUnableToModifiedWarning("unable-to-modify-warning"));
        }

        if (this.isInvalid)
            render.push(this.renderInvalid("invalid-overlay"));

        if (render.length == 0)
            return null;

        return (
            <MedicalRequestFormRowComponent dataCy="SystemMessagesRow" className={this.styles.systemMessagesRow}>
                {render}
            </MedicalRequestFormRowComponent>
        );
    }

    private renderLinkRemovalWarningModalWindow(): JSX.Element {
        const actionText = this.isPrintedOrFaxedOrSyncedToPortal ? cacheStore.GetString("InvalidateLabel") : cacheStore.GetString("DeleteLabel");
        const title = `${cacheStore.GetString("WarningLabel")} - ${actionText} ${cacheStore.GetString(this._medicalRequestHandler.titleLabel)}`;

        const continueAction = this.isPrintedOrFaxedOrSyncedToPortal
            ? () => this.toggleIsInvalidConfirmed(this.state.model.id, this.state.model.encounterId, true)
            : () => this.deleteConfirmed();

        const cancelAction = (): void => this.setShowLinkRemovalWarning(false);

        const messageElement = (
            <div style={{ paddingLeft: "20px" }}>
                <div>
                    <span>
                        {cacheStore.GetString(
                            this.isPrintedOrFaxedOrSyncedToPortal
                                ? this._medicalRequestHandler.invalidateWarningLabel
                                : this._medicalRequestHandler.deleteWarningLabel
                        )}
                    </span>
                </div>
                <div style={{ marginTop: "15px" }}>
                    <span>{cacheStore.GetString("ActionIrreversableContinueLabel")}</span>
                </div>
            </div>
        );

        return this.renderWarningModalWindow(title, messageElement, continueAction, cancelAction);
    }

    private renderDeleteWarningModalWindow(): JSX.Element {
        const title = `${cacheStore.GetString("WarningLabel")} - ${cacheStore.GetString("DeleteLabel")} ${cacheStore.GetString(this._medicalRequestHandler.titleLabel)}`;

        const continueAction = (): void => this.deleteConfirmed();
        const cancelAction = (): void => this.setShowDeleteWarning(false);

        const messageElement = (
            <div style={{ paddingLeft: "20px" }}>
                <div>
                    <span>
                        {cacheStore.GetString(
                            "DeleteMedicalRequestWarningLabel",
                            { medicalRequestLabel: cacheStore.GetString(this._medicalRequestHandler.titleLabel).toLowerCase() }
                        )}
                    </span>
                </div>
            </div>
        );

        return this.renderWarningModalWindow(title, messageElement, continueAction, cancelAction);
    }

    private renderWarningModalWindow(title: string, message: JSX.Element, continueAction: () => void, cancelAction: () => void): JSX.Element {
        return (
            <Modal
                title={title}
                showOverlay={true}
                draggable={true}
                footer={this.renderWarningModalWindowFooter(continueAction, cancelAction)}
                onClose={cancelAction}
            >
                {message}
            </Modal>
        );
    }

    private renderWarningModalWindowFooter(continueAction: () => void, cancelAction: () => void): JSX.Element {
        return (
            <div className="flex-container" style={{ "justifyContent": "flex-end" }}>
                <div style={{ "marginLeft": "5px" }}>
                    <button className="btn btn-default" onClick={cancelAction}>
                        {cacheStore.GetString("CancelLabel")}
                    </button>
                </div>
                <div style={{ "marginLeft": "5px" }}>
                    <button
                        className="btn btn-danger"
                        data-cy="medicalRequestPopup-warningContinue"
                        onClick={continueAction}
                    >
                        {cacheStore.GetString("ContinueLabel")}
                    </button>
                </div>
            </div>
        );
    }

    private renderGenerateBy(model: IMedicalRequest): JSX.Element | null {
        const [employeeId, generatedDate] = this.getGenerationInformation(model);
        const employee = cacheStore.GetCliniciansPersonsByContactFacetId(employeeId);

        if (!employee)
            return null;

        const date = moment(generatedDate).format("YYYY-MM-DD");
        const time = moment(generatedDate).format("HH:mm:ss");
        return (
            <p>
                {cacheStore.GetString("GeneratedByLabel")} <strong>{employee.NameWithPrefix}</strong> {cacheStore.GetString("OnDateLabel")} {date} {cacheStore.GetString("AtLabel")} {time}
            </p>
        );
    }

    private canBuildGeneratedBy(model: IMedicalRequest): boolean {
        const [employeeId, generatedDate] = this.getGenerationInformation(model);
        return (!!employeeId && !!generatedDate);
    }

    private buildGeneratedComment(model: IMedicalRequest): Comment {
        const [employeeId, generatedDate] = this.getGenerationInformation(model);
        const comment = new Comment();

        comment.CreateDate = generatedDate;
        comment.EmployeeId = employeeId;
        comment.Value = cacheStore.GetString("ConsultationRequestAction_RequestGenerated");

        return comment;
    }

    private getGenerationInformation(model: IMedicalRequest): [Guid, Date] {
        let employeeId: Guid = model.firstPrintedByEmployeeId;
        let generatedDate: Date = new Date(model.firstPrintedDate);

        if (this.props.encounter?.isSignedOff) {
            const signedOffDate = new Date(this.props.encounter.SignedOffDate);
            if (!generatedDate || signedOffDate < generatedDate) {
                generatedDate = signedOffDate;
                employeeId = this.props.encounter.SignedByEmployeeId;
            }
        }

        if (!employeeId || !generatedDate)
            return [null, null];

        return [employeeId, generatedDate];
    }

    private renderUnableToModifiedWarning(key: string): JSX.Element {
        return (
            <p key={key} style={{ padding: "10px 0", color: "red", textAlign: "center" }}>
                {cacheStore.GetString(this._medicalRequestHandler.unableToModifyWarningLabel)}
            </p>
        );
    }

    private renderAbleToModifyFromPatientFileOnlyWarning(key: string): JSX.Element {
        return (
            <p key={key} style={{ padding: "10px 0", color: "red", textAlign: "center" }}>
                {cacheStore.GetString(this._medicalRequestHandler.modifyRequestFromPatientFileWarningLabel)}
            </p>
        );
    }

    private renderInvalid(key: string): JSX.Element {
        return (
            <div key={key} className="medical-request-invalid">
                <h1>
                    {cacheStore.GetString("InvalidLabel")}
                </h1>
            </div>
        );
    }

    private renderLinkCareElement(): JSX.Element {
        if (!cacheStore.ClinicSettings.EnableFollowUps || this.props.isDisplayAsReadonly || this.isInvalid || this.props.hideLinkedCareSection)
            return null;

        if (!this.state.linkedElement && this.isEncounterSignedOff)
            return null;

        return (
            <LinkCareElementComponent
                key="LinkCareElementKey"
                elements={this.state.careElements}
                linkedElement={this.state.linkedElement}
                patientDoB={this.props.patientDoB}
                showHasNoCareElements={this.hasNoCareElements()}
                infoMessage={this.getUnableToModifyLinkedCareElementInfoLabel()}
                disabledFormFields={this.disableFormFieldsForLinkedCareElement()}
                disableSelection={this.disableLinkedCareElementSelection()}
                onSelectedElementChanged={selected => this.mutateState(s => {
                    s.selectedCareElement = selected;
                    if (s.selectedCareElement)
                        s.comment = "";
                })}
                shouldSetSelectElementToPendingStatus={this.state.model && MedicalRequestStatus.isInPendingStatus(this.state.model.statusLookupId)}
            />
        );
    }

    private renderStatusSection(): JSX.Element {
        if (this.shouldHideStatusSection())
            return null;

        return (
            <MedicalRequestStatusSection
                key="medicalRequestStatusSection"
                model={this.state.model}
                titleLabel={this._medicalRequestHandler.statusSectionTitleLabel}
                comment={this.state.comment}
                hideCommentInput={this.isCommentInputHidden}
                onStatusChanged={statusId => this.mutateModelState(m => m.statusLookupId = statusId)}
                onCommentChanged={comment => this.mutateState(s => s.comment = comment)}
                onDueDateChanged={date => {
                    this.mutateModelState(m => {
                        m.dueDateText = date.text;
                        m.dueDate = date.value;
                    });
                }}
                onConsultationDateTimeChange={date => {
                    this.mutateModelState((m: ConsultationRequest) => {
                        const nextDateText = DateHelper.formatIsoDateOnly(date);
                        const nextDate = !!date ? DateTime.fromJSDate(date) : null;

                        m.referralConsultationDateText = nextDateText;
                        m.referralConsultationDate = nextDate;
                    });
                }}
                onRecipientChange={recipient => this.mutateModelState((m: IMedicalRequest) => m.recipient = recipient)}
                onRecipientSearchTextChange={searchText => this.mutateModelState((m: IMedicalRequest) => {
                    m.recipientContactDirectoryEntityId = null;
                    m.recipientDirectorySelectedEntity = null;
                    m.recipient = searchText;
                    m.recipientSearchText = searchText;
                })}
                onContactDirectoryEntityChanged={directoryEntity => this.mutateModelState((state: IMedicalRequest) => {
                    state.recipientContactDirectoryEntityId = directoryEntity.id;
                    state.recipient = directoryEntity.model.getDisplayName();
                    state.recipientDirectorySelectedEntity = directoryEntity;
                    state.recipientContactDirectoryEntityType = directoryEntity.type;
                })}
                onClearRecipient={() => this.mutateModelState(p => {
                    p.recipientContactDirectoryEntityId = null;
                    p.recipient = null;
                    p.recipientDirectorySelectedEntity = null;
                    p.recipientContactDirectoryEntityType = null;
                    p.recipientSearchText = null;
                })}
            />
        );
    }

    private renderIsVisibleInCPPCheckbox(className?: string, style?: React.CSSProperties): JSX.Element {
        if (this.isVisibleInCppCheckboxHidden())
            return null;

        return (
            <div className={className} style={style}>
                <CheckboxControl
                    checked={!this.state.model.isVisibleInCPP}
                    label={cacheStore.GetString("HideFromCPPAndClosedLabel")}
                    onChange={isChecked => this.onChangeVisibleInCPP(isChecked)}
                    dataCy="medicalRequestPopup-hideFromCPP"
                />
            </div>
        );
    }

    private renderFooter(): JSX.Element {
        const renderButtons: IMedicalRequestFooterButton = this.footerButtons;

        return (
            <div className="row p-footer">
                <div className="flex-container" style={{ justifyContent: "space-between" }} data-cy="medicalRequestPopup-footer">
                    <div>
                        {this.renderFooterButton(renderButtons.invalidButton, "btn btn-danger push-left-5")}
                        {this.renderFooterButton(renderButtons.reinstateButton, "btn btn-green push-left-5")}
                    </div>

                    <div>
                        {this.renderIsVisibleInCPPCheckbox("push-right-5", { display: "inline" })}
                        {this.renderFooterButton(renderButtons.cancelButton, "btn btn-default push-right-5")}
                        {this.renderPortalFooterButton(renderButtons.portalButton)}
                        {this.renderFooterButton(renderButtons.deleteButton, "btn btn-danger push-right-5")}
                        {this.renderFooterButton(renderButtons.signButton, "btn btn-primary push-right-5")}
                        {this.renderFooterButton(renderButtons.printButton, "btn btn-primary push-right-5")}
                        {this.renderFooterButton(renderButtons.faxButton, "btn btn-primary push-right-5")}
                        {this.renderFooterButton(renderButtons.useSignedCopyButton, "btn btn-primary push-right-5")}
                        {this.renderFooterButton(renderButtons.saveButton, "btn btn-primary push-right-5")}
                        {this.renderFooterButton(renderButtons.sendButton, "btn btn-primary push-right-5")}
                    </div>
                </div>
            </div>
        );
    }

    private renderFooterButton(button: IMedicalRequestButtonProperties, className: string): JSX.Element {
        return (
            button?.show && (
                <button onClick={button.onClick} className={className} disabled={button.disabled}>
                    <span>{button.text}</span>
                </button>
            )
        );
    }

    private renderPortalFooterButton(properties: IMedicalRequestButtonProperties): JSX.Element {
        if (!properties?.show || !this.state.sharedItem)
            return null;

        const buttonProps = PortalHelper.getPortalButtonPropsForSyncStatus(
            this.state.sharedItem.Status,
            this.state.sharedItem.IncludeLinkedItem
        );

        return (
            <PortalStatusButton
                onClick={properties.onClick}
                disabled={properties.disabled}
                style={{ verticalAlign: "middle", marginRight: "5px" }}
                icon="share"
                {...buttonProps}
            />
        );
    }

    private getUnableToModifyLinkedCareElementInfoLabel(): string {
        if (this.isEncounterSignedOff)
            return cacheStore.GetString("CantModifyLinkedElementCauseEncounterIsApprovedLabel");

        if (this.isLinkedElementNotTheLatestEntry())
            return cacheStore.GetString(
                "CantModifyLinkedElementCauseTheEntryIsNotTheLatestLabel",
                {
                    medicalRequestLabel: cacheStore.GetString(this._medicalRequestHandler.titleLabel).toLowerCase()
                }
            );

        if (this.isSelectedElementEvolvedPastEncounter())
            return cacheStore.GetString("CantModifyLinkedElementCauseNewEntryIsAddedInNewerNoteLabel");

        if (this.hasLinkedElementEvolvedPastLinkEntry())
            return cacheStore.GetString("CantUnlinkedElementCauseNewEntryIsAddedInTheSameNoteLabel");

        return "";
    }

    private isMedicalFormReadOnly(): boolean {
        return this.props.isDisplayAsReadonly
            || this.isPrintedOrFaxedOrSyncedToPortal
            || this.isInvalid
            || this.isEncounterSignedOff;
    }

    private shouldHideStatusSection(): boolean {
        if (cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements)
            return false;

        return !cacheStore.ClinicSettings.EnableFollowUps
            || this.props.isDisplayAsReadonly
            || this.isInvalid;
    }

    private isVisibleInCppCheckboxHidden(): boolean {
        return this.shouldHideStatusSection() || !this.state.model;
    }

    private disableFormFieldsForLinkedCareElement(): boolean {
        return this.isEncounterSignedOff ||
            this.isSelectedElementEvolvedPastEncounter() ||
            this.isLinkedElementNotTheLatestEntry() ||
            this.isLinkedElementIsSignedOff();
    }

    private disableLinkedCareElementSelection(): boolean {
        return this.isEncounterSignedOff ||
            this.hasLinkedElementEvolvedPastLinkEntry();
    }

    private isLinkedElementIsSignedOff(): boolean {
        if (this.state.linkedElement && this.state.selectedCareElement && this.state.linkedElement.model.Id === this.state.selectedCareElement.model.Id)
            return this.state.linkedElement.isSignedOff;

        return false;
    }

    private isSelectedElementEvolvedPastEncounter(): boolean {
        if (!this.state.selectedCareElement || !this.props.encounter || !this.props.encounter.consultationDate)
            return false;

        return this.state.selectedCareElement.hasEvolvedPastEncounter(this.props.encounter.id, this.props.encounter.consultationDate);
    }

    private hasLinkedElementEvolvedPastLinkEntry(): boolean {
        if (this.state.linkedElement && this.state.selectedCareElement && this.state.linkedElement.model.Id === this.state.selectedCareElement.model.Id)
            return this.state.linkedElement.hasEvolvedPastLinkEntry;

        return false;
    }

    private isLinkedElementNotTheLatestEntry(): boolean {
        if (this.state.linkedElement && this.state.selectedCareElement && this.state.linkedElement.model.Id === this.state.selectedCareElement.model.Id)
            return !this.state.linkedElement.isLatestScreeningTest;

        return false;
    }

    private hasNoCareElements(): boolean {
        return this.state.isCareElementsLoaded && this.state.careElements.length === 0;
    }

    private get isEncounterSignedOff(): boolean {
        return this.props.encounter && this.props.encounter.isSignedOff;
    }

    private get isNewForm(): boolean {
        return !this.props.id && !!this.props.encounter;
    }

    private get isPrintedOrFaxedOrSyncedToPortal(): boolean {
        return this.state.model && (this.state.model.isPrinted || this.state.model.isFaxed || !!this.state.model.sharedItemId);
    }

    private get isInvalid(): boolean {
        return this.state.model && this.state.model.isInvalid;
    }

    private get isFormValid(): boolean {
        if (!this.state.model)
            return false;

        if (this.props.isDisplayAsReadonly)
            return true;

        if (this.isEncounterSignedOff || this.isPrintedOrFaxedOrSyncedToPortal)
            return !cacheStore.ClinicSettings.EnableFollowUps
                || (!!this.state.model.statusLookupId
                    && (!this.state.selectedCareElement || this._medicalRequestHandler.areCareElementFieldsValid(this.state.selectedCareElement))
                );

        return this._medicalRequestHandler.areMedicalRequestFieldsValid(this.state.model)
            && (!this.state.selectedCareElement || this._medicalRequestHandler.areCareElementFieldsValid(this.state.selectedCareElement));
    }

    private get showFaxBtn(): boolean {
        return (
            this.state.model
            && !this.isInvalid
            && (
                sessionStore.signOnPrintAccess
                || cacheStore.ClinicSettings.EnableQuickDocumentSigning
            )
            && (
                this.isNewForm
                || this.state.model.isSupervisorRequired
                || this.state.model.requestingStaffContactFacetId === this.state.currentEmployee.ContactFacetId
                || this.state.model.lastUpdatedByContactId === this.state.currentEmployee.ContactId
            )
        );
    }

    private get showSignBtn(): boolean {
        return (
            cacheStore.ClinicSettings.EnableSignedAuthorization
            && this.state.model
            && !this.isInvalid
            && (
                sessionStore.signOnPrintAccess
                || cacheStore.ClinicSettings.EnableQuickDocumentSigning
            )
            && (
                this.state.model.isSupervisorRequired
                || this.state.model.requestingStaffContactFacetId == this.state.currentEmployee.ContactFacetId
                || this.state.model.createdByContactId == this.state.currentEmployee.ContactId
            )
            && !this.isLastSignatureFromCurrentEmployee
        );
    }

    private get showUseSignedCopyBtn(): boolean {
        return cacheStore.ClinicSettings.EnableSignedAuthorization
            && this.state.model?.signedPrintAuthorizations?.any()
            && !this.isLastSignatureFromCurrentEmployee;
    }

    private get showPortalButton(): boolean {
        return sessionStore.signOnPrintAccess
            && this.state.model
            && !this.isInvalid
            && cacheStore.ClinicSettings_Portal.EnablePortalWebsite
            && this.props.isPatientRegisteredToPortal;
    }

    private get showSendBtn(): boolean {
        if (!(this.state.model instanceof LabRequest))
            return false;

        const model = this.state.model as LabRequest;

        return cacheStore.IsChristus()
            && model.type !== LabRequestType.None
            && this.isEditMode;
    }

    private get isEditMode(): boolean {
        return !this.props.isDisplayAsReadonly
            && !this.isPrintedOrFaxedOrSyncedToPortal
            && !this.isInvalid
            && !this.isEncounterSignedOff;
    }

    private get isLastSignatureFromCurrentEmployee(): boolean {
        return SignedPrintAuthorization.isLastSignatureFrom(
            this.state.model?.signedPrintAuthorizations,
            sessionStore.getCurrentEmployee().ContactFacetId
        );
    }

    private get isCommentInputHidden(): boolean {
        return cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements && this.props.type === MedicalRequestType.ConsultationRequest
            ? false
            : !!this.state.selectedCareElement && !this.isEncounterSignedOff;
    }

    private isButtonDisabled(existingCondition: boolean = false): boolean {
        return existingCondition || this.state.isButtonAlreadyClicked;
    }

    private setButtonClicked(then?: () => void): void {
        this.mutateState(
            s => s.isButtonAlreadyClicked = true,
            () => then?.()
        );
    }

    private get footerButtons(): IMedicalRequestFooterButton {
        return {
            reinstateButton: {
                show: !this.props.isDisplayAsReadonly && !this.isNewForm && this.isPrintedOrFaxedOrSyncedToPortal && this.isInvalid && !this.isEncounterSignedOff,
                disabled: this.isButtonDisabled(),
                text: cacheStore.GetString("ReinstateLabel"),
                onClick: () => this.setButtonClicked(() => this.toggleIsInvalid(this.state.model.id, this.state.model.encounterId, false))
            },
            invalidButton: {
                show: !this.props.isDisplayAsReadonly && !this.isNewForm && this.isPrintedOrFaxedOrSyncedToPortal && !this.isInvalid && !this.hasLinkedElementEvolvedPastLinkEntry() && !this.isEncounterSignedOff,
                disabled: this.isButtonDisabled(),
                text: cacheStore.GetString("InvalidateLabel"),
                onClick: () => this.setButtonClicked(() => this.toggleIsInvalid(this.state.model.id, this.state.model.encounterId, true))
            },
            deleteButton: {
                show: this.isEditMode && !this.isNewForm && !this.hasLinkedElementEvolvedPastLinkEntry(),
                disabled: this.isButtonDisabled(this.state.showEditOption),
                text: cacheStore.GetString("DeleteLabel"),
                onClick: () => this.setButtonClicked(() => this.delete())
            },
            editButton: {
                show: !this.isInvalid && this.state.showEditOption,
                disabled: false,
                text: cacheStore.GetString("EDITLabel"),
                onClick: () => this.handleEdit()
            },
            printButton: {
                show: !this.isInvalid,
                disabled: this.isButtonDisabled(!this.isFormValid),
                text: cacheStore.GetString("PrintLabel"),
                onClick: () => this.setButtonClicked(() => {
                    this.saveModalPreferences();
                    this.save(model => this.generatePreview(model.id, ExportMode.Print, false));
                })
            },
            cancelButton: {
                show: true,
                disabled: false,
                text: this.isInvalid ? cacheStore.GetString("CloseLabel") : cacheStore.GetString("CancelLabel"),
                onClick: () => this.props.onClose?.()
            },
            portalButton: {
                show: this.showPortalButton,
                disabled: this.isButtonDisabled(!this.isFormValid),
                text: "",
                onClick: () => this.setButtonClicked(() => this.save(model => this.portalClick(model)))
            },
            faxButton: {
                show: this.showFaxBtn,
                disabled: this.isButtonDisabled(!this.isFormValid),
                text: cacheStore.GetString("FaxVerbLabel"),
                onClick: () => this.setButtonClicked(() => {
                    this.saveModalPreferences();
                    this.save(model => this.generatePreview(model.id, ExportMode.Fax, false));
                })
            },
            signButton: {
                show: this.showSignBtn,
                disabled: this.isButtonDisabled(!this.isFormValid),
                text: cacheStore.GetString("SignLabel"),
                onClick: () => this.setButtonClicked(() => this.save((model, printInfo) => {
                    this.saveModalPreferences();
                    this.props.onSignClick?.(model, printInfo);
                    this.props.onClose?.();
                }))
            },
            useSignedCopyButton: {
                show: this.showUseSignedCopyBtn,
                disabled: this.isButtonDisabled(!this.isFormValid),
                text: cacheStore.GetString("UseSignedCopyLabel"),
                onClick: () => this.setButtonClicked(() => this.save(model => this.generateSignedPreview(model.id, true)))
            },
            saveButton: {
                show: this.isSaveButtonVisible,
                disabled: this.isButtonDisabled(!this.isFormValid),
                text: cacheStore.GetString("SaveLabel"),
                onClick: () => this.setButtonClicked(() => {
                    this.saveModalPreferences();
                    this.onSaveButtonClicked();
                })
            },
            sendButton: {
                show: this.showSendBtn,
                disabled: this.isButtonDisabled(!this.isFormValid),
                text: cacheStore.GetString("SendLabel"),
                onClick: () => this.setButtonClicked(() => this.handleSend())
            }
        };
    }

    private async save(then?: (model: IMedicalRequest, printInfo: PrintInfo) => void): Promise<void> {
        if (
            !cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements
            && (
                this.props.isDisplayAsReadonly
                || (!cacheStore.ClinicSettings.EnableFollowUps && then && !this.isEditMode)
                || (!this.formHasChanges() && !this.state.saveButtonClicked)
            )
        ) {
            // allow recipient change on view mode
            if (this.state.model instanceof ConsultationRequest) {
                const initialConsultRequest = this.state.initialModel as ConsultationRequest;
                const consultRequest = this.state.model as ConsultationRequest;
                if (consultRequest.recipient != initialConsultRequest.recipient) {
                    let response = await Client.postAsync(
                        ServerOperations.Server.Myle_Patient_MedicalNote_ModifyMedicalRequestRecipient,
                        {
                            MedicalRequestType: MedicalRequestType.ConsultationRequest,
                            MedicalRequestId: consultRequest.id,
                            Recipient: consultRequest.recipient,
                            RecipientContactDirectoryEntityId: consultRequest.recipientContactDirectoryEntityId,
                            RecipientContactDirectoryEntityType: consultRequest.recipientContactDirectoryEntityType
                        }
                    );
                    if (!response.isSuccess) {
                        sessionStore.showPopup(response.errors);
                        return;
                    }
                }
            }

            then(this.state.model, this.state.printInfoModel);
            return;
        }

        await this._medicalRequestAction.save(
            this.state.model,
            new LinkingCareElementRequest(this.state.linkedElement, this.state.selectedCareElement),
            this.state.comment,
            this.state.showEditOption && !cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements,
            this.state.attachments.map(x => x.ToAttachedObjectResponse()),
            (item, shouldSaveRelatedEmployee, printInfo) => {
                this.props.onSaveSuccess?.(item, shouldSaveRelatedEmployee, printInfo);
                this.mutateModelState(s => s.id = item.id, () => then?.(item, printInfo));

                if (this.props.closeAfterButtonClick && !then)
                    this.props.onClose?.();
            },
            errorMsgs => this.handleError(errorMsgs)
        );
    }

    private delete(): void {
        if (this.state.linkedElement && this.state.linkedElement.linkAddedNewEntry)
            this.setShowLinkRemovalWarning(true);
        else
            this.setShowDeleteWarning(true);
    }

    private deleteConfirmed(): void {
        this._medicalRequestAction.delete(
            this.state.model.id,
            this.state.model.encounterId,
            (requestId, encounterId) => {
                this.props.onDeleteSuccess && this.props.onDeleteSuccess(requestId, encounterId);

                if (this.props.closeAfterButtonClick)
                    this.props.onClose?.();
                else
                    this.state.showLinkRemovalWarning ? this.setShowLinkRemovalWarning(false) : this.setShowDeleteWarning(false);
            },
            errorMsgs => this.handleError(errorMsgs)
        );
    }

    private send(model: LabRequest, then?: (model: IMedicalRequest, SendStatus: string) => void): void {
        const action = LabRequestAction.getInstance();
        action.send(
            model,
            sendStatus => {
                then?.(model, sendStatus);

                if (this.props.closeAfterButtonClick)
                    this.props.onClose?.();
            },
            errors => {
                if (errors?.length)
                    sessionStore.showPopup(errors);
            }
        );
    }

    private load(id: string): void {
        this._medicalRequestAction.load(
            id,
            this.props.showPrintHeader || cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements,
            (item, patientPrintInfo, linkedElement, attachedDocuments) => {
                this.mutateState(s => {
                    s.currentEmployee = this.state.currentEmployee;

                    s.initialModel = this._medicalRequestHandler.cloneModel(item);

                    s.model = item;
                    if (s.model instanceof LabRequest) {
                        const labRequest = s.model as LabRequest;
                        s.selectedCostCenterId = labRequest.billCodes.firstOrDefault()?.CostCenterId ?? null;
                    }

                    s.showEditOption = !this.props.isDisplayAsReadonly && !s.model.isPrinted && !s.model.isFaxed && !this.isEncounterSignedOff
                        && !this.canEdit(s.model);

                    s.printInfoModel = patientPrintInfo;

                    s.sharedItem = item.sharedItem || new SharedItem();
                    s.sharedItemId = item.sharedItemId;
                    s.sharedItemMessages = item.sharedItemMessages || [];

                    s.linkedElement = linkedElement;
                    if (s.linkedElement) {
                        s.selectedCareElement = s.linkedElement;
                        s.careElements = this.state.careElements;
                        if (!s.careElements.some(x => x.model.MedicalItemId === s.linkedElement.model.MedicalItemId))
                            s.careElements.push(s.linkedElement);
                    }

                    s.attachments = attachedDocuments;
                    if (cacheStore.ClinicSettings.EnableConsultationRequestHistory && this.canBuildGeneratedBy(s.model)) {
                        s.model.comments = [...(s.model.comments ?? []), this.buildGeneratedComment(s.model)].sortByProperty(comment => comment.CreateDate);
                    }
                });
            },
            errorMsgs => this.handleError(errorMsgs)
        );
    }

    private loadCareElements(encounterId: string): void {
        if (!encounterId)
            return;

        const action = LinkedCareElementsAction.getInstance();
        action.loadAvailableCareElements(
            encounterId,
            careElements => {
                this.mutateState(s => {
                    s.careElements = this.state.careElements.concat(careElements);
                    s.isCareElementsLoaded = true;
                });
            },
            errorMsgs => this.handleError(errorMsgs)
        );
    }

    private toggleIsInvalid(id: Guid, encounterId: Guid, isInvalid: boolean): void {
        if (this.state.linkedElement?.linkAddedNewEntry)
            this.setShowLinkRemovalWarning(true);
        else {
            const sharedItemId = !IsNullOrWhiteSpace(this.state.sharedItemId)
                ? this.state.sharedItemId
                : "";
            this.toggleIsInvalidConfirmed(id, encounterId, isInvalid, sharedItemId);
        }
    }

    private toggleIsInvalidConfirmed(id: Guid, encounterId: Guid, isInvalid: boolean, sharedItemId?: string): void {
        this._medicalRequestAction.invalidate(
            id,
            encounterId,
            isInvalid,
            sharedItemId,
            (id, encounterId, isInvalid) => {
                this.props.onToggleIsInvalid?.(id, encounterId, isInvalid, sharedItemId);

                if (this.props.closeAfterButtonClick)
                    this.props.onClose?.();
                else
                    this.setShowLinkRemovalWarning(false);
            },
            errorMsgs => this.handleError(errorMsgs)
        );
    }

    private setShowLinkRemovalWarning(show: boolean): void {
        this.mutateState(s => {
            s.showLinkRemovalWarning = show;
            if (!show)
                s.isButtonAlreadyClicked = false;
        });
    }

    private setShowDeleteWarning(show: boolean): void {
        this.mutateState(s => {
            s.showDeleteWarning = show;
            if (!show)
                s.isButtonAlreadyClicked = false;
        });
    }

    private generatePreview(id: string, exportMode: ExportMode, withSignature: boolean = false): void {
        const signedPrintAuthorization = SignedPrintAuthorization.getLast(this.state.model.signedPrintAuthorizations);
        const isSignedByCurrentUser = SignedPrintAuthorization.isSignedBy(
            signedPrintAuthorization,
            sessionStore.getCurrentEmployee().ContactFacetId
        );

        this._medicalRequestAction.generatePDF(
            id,
            withSignature,
            false,
            this.state.attachments,
            (exportableFile, patient, attachedDocuments) => {
                const openPrintSignPopup =
                    (sessionStore.signOnPrintAccess || cacheStore.ClinicSettings.EnableQuickDocumentSigning)
                    && this.state.model.requestingStaffContactFacetId == this.state.currentEmployee.ContactFacetId;

                let config: IFaxPrintExportableFileConfig = {
                    exportableFile,
                    attachedDocuments,
                    exportMode: exportMode,
                    hideSignaturePinControl: !openPrintSignPopup,
                    patientContactId: patient.get("PatientContactId"),
                    isSaveSignatureEnabled: openPrintSignPopup,
                    onSignButtonCallback: () => this.props.loadSignedAuthorizations?.(exportableFile.getAttachedObject_ObjectId()),
                    onApplyOverlaysCallback: null,
                    syncToPortalAfterChecked: false,
                    isSyncToPortalAfterEnabled: this.props.isPatientRegisteredToPortal && !this.isModelSyncedToPortal(),
                    syncToPortalAfterAction: () => this.props.onSendToPortal?.(this.state.model),
                    preSignedCopy: exportableFile.clone().applyOverlayElement(new SignPrintAuthorizationOverlayElement({
                        SignedPrintAuthorization: signedPrintAuthorization
                    })),
                    useSignedCopyOverride: cacheStore.ClinicSettings.EnableSignedAuthorization && isSignedByCurrentUser,
                    selectedRecipient: this.state.model.recipientDirectorySelectedEntity ?? this.state.model.recipient,
                    sentFaxItemId: id,
                    defaultContext: patient.get("IntervenorsOnFile").toArray(),
                    onClose: () => this.props.onFaxPrintPopupClose?.()
                };

                sessionStore.dispatchMain(
                    LocalOperations.MainComponentViewOperations.FaxPrintExportableFile,
                    { config }
                );

                this.props.onClose();
            },
            errorMsgs => this.handleError(errorMsgs)
        );
    }

    private generateSignedPreview(id: string, withSignature: boolean = true): void {
        this._medicalRequestAction.generatePDF(
            id,
            withSignature,
            true,
            this.state.attachments,
            (exportableFile, patient, attachedDocuments) => {
                const config: IFaxPrintExportableFileConfig = {
                    exportableFile: null,
                    attachedDocuments,
                    exportMode: ExportMode.UseSigned,
                    hideSignaturePinControl: false,
                    patientContactId: patient.get("PatientContactId"),
                    isSaveSignatureEnabled: false,
                    onApplyOverlaysCallback: null,
                    syncToPortalAfterChecked: false,
                    isSyncToPortalAfterEnabled: false,
                    defaultContext: patient.get("IntervenorsOnFile").toArray(),
                    preSignedCopy: exportableFile.clone().applyOverlayElement(new SignPrintAuthorizationOverlayElement({
                        SignedPrintAuthorization: SignedPrintAuthorization.getLast(this.state.model.signedPrintAuthorizations ?? [])
                    })),
                    selectedRecipient: this.state.model.recipientDirectorySelectedEntity ?? this.state.model.recipient,
                    fileType: this.getExportableFileType()
                };

                sessionStore.dispatchMain(
                    LocalOperations.MainComponentViewOperations.FaxPrintExportableFile,
                    { config }
                );

                this.props.onClose();
            },
            errorMsgs => this.handleError(errorMsgs)
        );
    }

    private portalClick(model: IMedicalRequest): void {
        const showWarningToSyncToPortal = PortalHelper.showWarningToSyncToPortal(
            model.sharedItem,
            model.requestingStaffContactFacetId,
            model.createdByContactId,
            null
        );

        if (showWarningToSyncToPortal) {
            sessionStore.showPopup({
                title: cacheStore.GetString("WarningLabel"),
                content: [cacheStore.GetString("WarningSignRequestingStaffPortalLabel")]
            });
        }
        else
            this.props.onSendToPortal?.(model);

        if (this.props.closeAfterButtonClick)
            this.props.onClose?.();
    }

    private handleEdit(): void {
        this.mutateState(s => {
            s.showEditOption = false;
            s.model = this.state.model;

            if (s.model.isSupervisorRequired) {
                const lastModifiedEmployee = cacheStore.GetEmployeeByContactIdOrDefault(s.model.lastUpdatedByContactId);
                if (lastModifiedEmployee) {
                    const employeeUI = EmployeeUIModel.fromCompositeObject(lastModifiedEmployee);
                    s.model.requestingStaffContactFacetId = employeeUI.shouldHaveSupervisorBasedOnTitle()
                        ? s.model.requestingStaffContactFacetId
                        : null;
                }
            }
            else {
                s.model.requestingStaffContactFacetId = this.state.currentEmployee.ContactFacetId;
            }
        });
    }

    private handleSend(): void {
        if (this.isPrintedOrFaxedOrSyncedToPortal) {
            this.send(this.state.model as LabRequest, this.props.onSendCallback);
        }
        else {
            this.save(model => this.send(model as LabRequest, this.props.onSendCallback));
        }
    }

    private handleError(errorMsgs: ErrorMessage[]): void {
        if (isArrayNullOrUndefinedOrEmpty(errorMsgs))
            return;

        sessionStore.showPopup(errorMsgs);

        if (this.props.closeAfterButtonClick)
            this.props.onClose?.();
    }

    private initNewModel(encounterId: string, patientContactId: Guid): void {
        const modalPreferences = this.getModalPreferences();
        const isVisibleInCumulativePatientProfile = !modalPreferences?.isHiddenFromCumulativePatientProfile;

        this.mutateState(s => {
            s.currentEmployee = this.state.currentEmployee;
            s.model = this._medicalRequestHandler.createModel(s.currentEmployee, encounterId, this.props.labRequestType, isVisibleInCumulativePatientProfile);

            if (!s.model.isVisibleInCPP && cacheStore.ClinicSettings.EnableQuickDocumentSigning)
                s.model.statusLookupId = this.getStatusBasedOnCPP(s.model);
        });

        if (this.props.type === MedicalRequestType.ConsultationRequest &&
            (
                cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements
                || cacheStore.ClinicSettings.EnableDefaultAttachmentsOnConsultationRequests
            )
        ) {
            const defaultAttachments: ExportableFileModel[] = [];

            if (settingsStore.configuration.get("ShouldAttachMedicalSummaryWhenCreatingConsultationRequest")) {
                const fileTypeMedicalSummary = new FileTypeMedicalSummary(patientContactId).toExportableFileModel(ExportableFileDisplayOption.NAME);
                defaultAttachments.push(fileTypeMedicalSummary);
            }

            if (settingsStore.configuration.get("ShouldAttachMedicationSummaryWhenCreatingConsultationRequest")) {
                const fileTypeMedicationSummary = new FileTypeMedicationSummary(patientContactId).toExportableFileModel(ExportableFileDisplayOption.NAME);
                defaultAttachments.push(fileTypeMedicationSummary);
            }

            this.mutateState(s => s.attachments = defaultAttachments);
        }
    }

    private canEdit(model: IMedicalRequest): boolean {
        const currentEmployee = this.state.currentEmployee;
        const lastModifiedEmployee = cacheStore.GetEmployeeByContactIdOrDefault(model.lastUpdatedByContactId);
        const createdEmployee = cacheStore.GetEmployeeByContactIdOrDefault(model.createdByContactId);

        return model.lastUpdatedByContactId === currentEmployee.ContactId
            || model.requestingStaffContactFacetId === currentEmployee.ContactFacetId
            || (
                createdEmployee &&
                lastModifiedEmployee &&
                model.requestingStaffContactFacetId === lastModifiedEmployee.ContactFacet_id &&
                createdEmployee.Contact_id === currentEmployee.ContactId
            );
    }

    private isModelSyncedToPortal(): boolean {
        if (this.state.model?.sharedItem) {
            const portalStatus = PortalHelper.getLinkedItemPortalStatus(this.state.model.sharedItem);
            return portalStatus === PortalSyncStatus.Pending || portalStatus === PortalSyncStatus.Viewed;
        }

        return false;
    }

    private formHasChanges(): boolean {
        return !this.state.initialModel
            || this._medicalRequestHandler.areFieldsDifferent(this.state.initialModel, this.state.model)
            || !!this.state.comment;
    }

    private onSaveButtonClicked(): void {
        this.mutateState(
            s => s.saveButtonClicked = true,
            () => this.save()
        );
    }

    private onChangeVisibleInCPP(isChecked: boolean): void {
        this.mutateModelState(m => {
            m.isVisibleInCPP = !isChecked;
            m.statusLookupId = this.getStatusBasedOnCPP(m);
        });
    }

    private getStatusBasedOnCPP(model: IMedicalRequest): string {
        let setStatus = null;
        if (!model.isVisibleInCPP)
            setStatus = MedicalRequestStatus.Closed;
        else if (!this.isMedicalFormReadOnly())
            setStatus = MedicalRequestStatus.Pending;

        return setStatus?.LookupID ?? model.statusLookupId;
    }

    private onCloseAllClickableTextPanel(): void {
        this.mutateState(p => {
            p.isReasonClickableTextPanelOpen = false;
            p.isDescriptionClickableTextPanelOpen = false;
        });
    }

    private saveModalPreferences(): void {
        const modalPreferences = {
            isHiddenFromCumulativePatientProfile: !this.state.model?.isVisibleInCPP
        };

        switch (this.props.type) {
            case MedicalRequestType.LabRequest:
                sessionStore.dispatch(LocalPatientOperations.MedicalNote.UpdateUserSettingsExamRequest, {
                    settings: modalPreferences
                });
                break;

            case MedicalRequestType.ConsultationRequest:
                sessionStore.dispatch(LocalPatientOperations.MedicalNote.UpdateUserSettingsConsultationRequest, {
                    settings: modalPreferences
                });
                break;
        }
    }

    private getExportableFileType(): FileType {
        switch (this.props.type) {
            case MedicalRequestType.LabRequest:
                return FileType.EXAMREQUEST;

            case MedicalRequestType.ConsultationRequest:
                return FileType.CONSULTATIONREQUEST;

            default:
                return null;
        }
    }

    private getModalPreferences(): PatientProfileRequestModalSettingsModel {
        switch (this.props.type) {
            case MedicalRequestType.LabRequest:
                return PatientProfileRequestModalSettingsModel.fromCacheModel(cacheStore.UserSettings.ExamRequestModal);

            case MedicalRequestType.ConsultationRequest:
                return PatientProfileRequestModalSettingsModel.fromCacheModel(cacheStore.UserSettings.ConsultationRequestModal);

            default:
                return { isHiddenFromCumulativePatientProfile: false };
        }
    }

    private get isSaveButtonVisible(): boolean {
        return cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements
            || (
                cacheStore.ClinicSettings.EnableFollowUps
                    ? !this.isInvalid && !this.props.isDisplayAsReadonly
                    : this.isEditMode || this.isNewForm
            );
    }

    private get isConsultationRequest(): boolean {
        return this.props.type === MedicalRequestType.ConsultationRequest;
    }
}

export { IMedicalRequestPopupControllerProps, MedicalRequestPopupController };
